import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import rubikBase64 from '../fonts/rubik-font';
import { saveAs } from 'file-saver';
import api from '../api/axios';
import '../styles/attendance.css';

const STANDARD_HOURS = 8.5;

// Helper function - defined outside component to avoid hoisting issues
const isWeekend = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone issues
  const day = date.getDay();
  return day === 5 || day === 6; // Friday or Saturday (Israeli weekend)
};

const Attendance = () => {
  const { user: currentUser } = useAuth();
  const isManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const [searchParams, setSearchParams] = useSearchParams();
  const viewUserId = searchParams.get('user_id');
  const [viewUserName, setViewUserName] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [holidays, setHolidays] = useState(new Set());
  const [holidayNames, setHolidayNames] = useState({});
  const [holidayTypes, setHolidayTypes] = useState({});
  const [month, setMonth] = useState(() => {
    const urlMonth = new URLSearchParams(window.location.search).get('month');
    if (urlMonth && /^\d{4}-\d{2}$/.test(urlMonth)) return urlMonth;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calOpen, setCalOpen] = useState(false);
  const [calYear, setCalYear] = useState(() => Number(month.split('-')[0]));
  const calRef = useRef(null);

  const [monthLocked, setMonthLocked] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  // Close calendar dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch lock status for the displayed month
  useEffect(() => {
    const params = { month };
    if (viewUserId) params.user_id = viewUserId;
    api.get('/attendance/lock', { params })
      .then(res => setMonthLocked(res.data.data?.locked || false))
      .catch(() => setMonthLocked(false));
  }, [month, viewUserId]);

  // Fetch Israeli holidays for the displayed year
  useEffect(() => {
    const year = parseInt(month.split('-')[0]);
    api.get('/holidays', { params: { year } }).then(res => {
      const data = res.data.data || [];
      setHolidays(new Set(data.filter(h => h.type === 'holiday').map(h => h.date)));
      const names = {};
      data.forEach(h => { names[h.date] = h.name; });
      setHolidayNames(names);
      const types = {};
      data.forEach(h => { types[h.date] = h.type; });
      setHolidayTypes(types);
    }).catch(() => {});
  }, [month]);

  const isHolidayDate = useCallback((dateStr) => {
    return holidays.has(dateStr);
  }, [holidays]);

  const getDefaultStatus = useCallback((dateStr) => {
    if (isWeekend(dateStr)) return 'weekend';
    if (holidays.has(dateStr)) return 'hag';
    if (holidayTypes[dateStr] === 'half_day') return 'erev_hag';
    return '--';
  }, [holidays, holidayTypes]);

  const fetchAttendances = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (viewUserId) params.user_id = viewUserId;
      const response = await api.get('/attendance', { params });
      const records = response.data.data.data || [];

      // Capture viewed user's name from first record
      if (viewUserId && records.length > 0 && records[0].user_name) {
        setViewUserName(records[0].user_name);
      } else if (viewUserId && !viewUserName) {
        // Fetch user name even if no records exist
        try {
          const userRes = await api.get(`/users/${viewUserId}`);
          setViewUserName(userRes.data.data?.user?.name || null);
        } catch {}
      }

      // Generate all days of the month
      const [year, monthNum] = month.split('-').map(Number);
      const daysInMonth = new Date(year, monthNum, 0).getDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const allDays = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, monthNum - 1, day);
        const dateStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        // Find ALL existing records for this date (supports multiple sessions per day)
        const dayRecords = records.filter(r => {
          if (!r.date) return false;
          const recordDateStr = String(r.date).slice(0, 10);
          return recordDateStr === dateStr;
        }).sort((a, b) => {
          // Sort by clock_in time ascending
          if (!a.clock_in) return 1;
          if (!b.clock_in) return -1;
          return new Date(a.clock_in) - new Date(b.clock_in);
        });

        const isFuture = date > today;

        if (dayRecords.length > 0) {
          // Add each session as a separate row
          dayRecords.forEach((existing, idx) => {
            allDays.push({
              id: existing.id,
              date: dateStr,
              clock_in: existing.clock_in || null,
              clock_out: existing.clock_out || null,
              total_hours: existing.total_hours || null,
              note: existing.note || '',
              status: existing.status || getDefaultStatus(dateStr),
              isNew: false,
              isPast: date < today,
              isToday: date.toDateString() === today.toDateString(),
              isFuture,
              sessionNum: dayRecords.length > 1 ? idx + 1 : null,
            });
          });
        } else {
          // Empty row (no records or future day)
          allDays.push({
            id: `new-${dateStr}`,
            date: dateStr,
            clock_in: null,
            clock_out: null,
            total_hours: null,
            note: '',
            status: getDefaultStatus(dateStr),
            isNew: true,
            isPast: date < today,
            isToday: date.toDateString() === today.toDateString(),
            isFuture,
            sessionNum: null,
          });
        }
      }

      setAttendances(allDays); // Ascending order: day 1, 2, 3...
    } catch (error) {
      console.error('Error fetching attendances:', error);
      toast.error('Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  }, [month, viewUserId, isHolidayDate, getDefaultStatus]);

  useEffect(() => {
    fetchAttendances();
  }, [fetchAttendances]);

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    if (searchParams.get('month') === month) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('month', month);
    setSearchParams(nextParams, { replace: true });
  }, [month, searchParams, setSearchParams]);

  const [statusDropdownPos, setStatusDropdownPos] = useState(null);
  const [statusDropdownRecord, setStatusDropdownRecord] = useState(null);
  const statusDropdownRef = useRef(null);

  const [notePopup, setNotePopup] = useState(null); // { record, pos }
  const [noteText, setNoteText] = useState('');
  const notePopupRef = useRef(null);

  const [attachPopup, setAttachPopup] = useState(null); // { record, pos }
  const [attachments, setAttachments] = useState({}); // { 'YYYY-MM-DD': [{ id, filename, url }] }
  const [uploading, setUploading] = useState(false);
  const attachPopupRef = useRef(null);
  const fileInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!statusDropdownRecord) return;
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target) && !e.target.closest('.status-badge-wrapper')) {
        setStatusDropdownRecord(null);
        setStatusDropdownPos(null);
        setEditingId(null);
        setEditValues({});
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownRecord]);

  // Close note popup when clicking outside
  useEffect(() => {
    if (!notePopup) return;
    const handleClickOutside = (e) => {
      if (notePopupRef.current && !notePopupRef.current.contains(e.target) && !e.target.closest('.note-icon')) {
        setNotePopup(null);
        setNoteText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notePopup]);

  // Close attachment popup when clicking outside
  useEffect(() => {
    if (!attachPopup) return;
    const handleClickOutside = (e) => {
      if (attachPopupRef.current && !attachPopupRef.current.contains(e.target) && !e.target.closest('.attach-icon')) {
        setAttachPopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [attachPopup]);

  // Fetch attachments for the month
  const fetchAttachments = useCallback(async () => {
    try {
      const fileParams = { month };
      if (viewUserId) fileParams.user_id = viewUserId;
      const response = await api.get('/attendance/files', { params: fileParams });
      const files = response.data.data || [];
      const grouped = {};
      files.forEach(f => {
        // Convert date to Israel timezone YYYY-MM-DD format
        const dateStr = new Date(f.date).toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' });
        if (!grouped[dateStr]) grouped[dateStr] = [];
        grouped[dateStr].push(f);
      });
      setAttachments(grouped);
    } catch {
      // Silently fail - attachments are optional
    }
  }, [month, viewUserId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const openNotePopup = (record, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setNotePopup({ record, pos: { top: rect.bottom + 4, left: rect.left - 200 } });
    setNoteText(record.note || '');
  };

  const saveNote = async () => {
    if (!notePopup) return;
    const record = notePopup.record;
    const dateStr = record.date;

    // Update locally immediately
    setAttendances(prev => prev.map(a =>
      a.date === dateStr ? { ...a, note: noteText } : a
    ));
    setNotePopup(null);

    try {
      const clockIn = record.clock_in || null;
      const clockOut = record.clock_out || null;
      await api.post('/attendance', {
        date: dateStr,
        clock_in: clockIn,
        clock_out: clockOut,
        status: record.status === '--' ? null : record.status,
        note: noteText,
      });
    } catch {
      toast.error('Failed to save note');
    }
    setNoteText('');
  };

  const openAttachPopup = (record, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAttachPopup({ record, pos: { top: rect.bottom + 4, left: rect.left - 250 } });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !attachPopup) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('date', attachPopup.record.date);
      const response = await api.post('/attendance/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newFile = response.data.data;
      setAttachments(prev => ({
        ...prev,
        [attachPopup.record.date]: [...(prev[attachPopup.record.date] || []), newFile],
      }));
      toast.success('File uploaded');
    } catch {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileDelete = async (dateStr, fileId) => {
    setAttachments(prev => ({
      ...prev,
      [dateStr]: (prev[dateStr] || []).filter(f => f.id !== fileId),
    }));
    try {
      await api.delete(`/attendance/files/${fileId}`);
    } catch {
      toast.error('Failed to delete file');
      fetchAttachments();
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await api.get(`/attendance/files/${file.id}`, {
        responseType: 'blob',
      });
      saveAs(response.data, file.filename);
    } catch {
      toast.error('Failed to download file');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone issues
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    // Handle both ISO format and MySQL format (space-separated)
    const normalized = String(timeStr).replace(' ', 'T');
    const date = new Date(normalized.endsWith('Z') ? normalized : normalized + 'Z');
    return date.toLocaleTimeString('en-GB', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatTimeForInput = (timeStr) => {
    if (!timeStr) return '--:--';
    // Handle both ISO format and MySQL format (space-separated)
    const normalized = String(timeStr).replace(' ', 'T');
    const date = new Date(normalized.endsWith('Z') ? normalized : normalized + 'Z');
    return date.toLocaleTimeString('en-GB', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Convert Israel time (HH:MM) to UTC datetime string for storage
  const israelToUTC = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    // If time contains dashes (incomplete), return null
    if (timeStr.includes('-')) return null;
    // Find Israel timezone offset for this date using a known reference
    const ref = new Date(`${dateStr}T12:00:00Z`);
    const israelRef = ref.toLocaleTimeString('en-GB', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit', hour12: false });
    const [refIH, refIM] = israelRef.split(':').map(Number);
    const offsetMinutes = (refIH * 60 + refIM) - 720; // difference from 12:00 UTC

    const [h, m] = timeStr.split(':').map(Number);
    let totalMinutes = (h * 60 + m) - offsetMinutes;

    const dateAdj = new Date(`${dateStr}T12:00:00Z`);
    if (totalMinutes < 0) {
      totalMinutes += 1440;
      dateAdj.setUTCDate(dateAdj.getUTCDate() - 1);
    } else if (totalMinutes >= 1440) {
      totalMinutes -= 1440;
      dateAdj.setUTCDate(dateAdj.getUTCDate() + 1);
    }

    const utcH = Math.floor(totalMinutes / 60);
    const utcM = totalMinutes % 60;
    const adjDateStr = dateAdj.toISOString().slice(0, 10);
    return `${adjDateStr} ${String(utcH).padStart(2, '0')}:${String(utcM).padStart(2, '0')}`;
  };

  // Convert decimal hours to HH:MM format (e.g., 2.5 -> "2:30")
  const formatHours = (decimalHours) => {
    if (!decimalHours && decimalHours !== 0) return '--';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  };

  // Format time input with dashes for missing digits (e.g., "1" -> "1-:--", "12" -> "12:--")
  const formatTimeWithDashes = (digits) => {
    if (digits.length === 0) return '--:--';
    const padded = digits.padEnd(4, '-');
    return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
  };

  // Calculate daily totals and per-session extra hours
  const dailyTotals = useMemo(() => {
    const totals = {};
    attendances.forEach(record => {
      const dateStr = record.date;
      if (!totals[dateStr]) {
        totals[dateStr] = { totalHours: 0, isFirstRow: true };
      }
      if (record.total_hours && (record.clock_in || record.clock_out)) {
        totals[dateStr].totalHours += parseFloat(record.total_hours) || 0;
      }
    });
    return totals;
  }, [attendances]);

  // Per-session extra hours: track cumulative hours and compute each session's extra contribution
  const sessionExtras = useMemo(() => {
    const extras = {};
    const cumulative = {};
    attendances.forEach(record => {
      const dateStr = record.date;
      if (!cumulative[dateStr]) cumulative[dateStr] = 0;
      const sessionHours = (record.total_hours && (record.clock_in || record.clock_out)) ? (parseFloat(record.total_hours) || 0) : 0;
      const prevCum = cumulative[dateStr];
      cumulative[dateStr] += sessionHours;
      const dayStd = holidayTypes[dateStr] === 'half_day' ? STANDARD_HOURS / 2 : STANDARD_HOURS;
      if (sessionHours > 0 && cumulative[dateStr] > dayStd) {
        extras[record.id] = Math.min(sessionHours, cumulative[dateStr] - dayStd);
      } else {
        extras[record.id] = 0;
      }
    });
    return extras;
  }, [attendances, holidayTypes]);

  // Track which dates have already shown their first row
  const seenDates = useRef(new Set());

  const getStatusBadge = (status, isNew) => {
    const statusMap = {
      '--': { label: '--', class: 'status-none' },
      present: { label: 'Present', class: 'status-present' },
      late: { label: 'Late', class: 'status-late' },
      absent: { label: 'Absent', class: 'status-absent' },
      approved_leave: { label: 'Approved Leave', class: 'status-approved-leave' },
      training: { label: 'Training', class: 'status-training' },
      holiday: { label: 'Holiday', class: 'status-holiday' },
      weekend: { label: 'Weekend', class: 'status-weekend' },
      erev_hag: { label: 'Erev Hag', class: 'status-half' },
      vacation: { label: 'Vacation', class: 'status-vacation' },
      hag: { label: 'Hag', class: 'status-holiday' },
      erev_hag: { label: 'Erev Hag', class: 'status-half' },
      half_day_off: { label: 'Half-Day Off', class: 'status-half' },
      sick_leave: { label: 'Sick Leave', class: 'status-sick' },
      child_sick: { label: 'Child Sick Leave', class: 'status-child-sick' },
      miluim: { label: 'Reserve Duty', class: 'status-miluim' },
      childcare: { label: 'Personal Childcare', class: 'status-childcare' },
      business_trip: { label: 'Business Trip Abroad', class: 'status-business-trip' },
      wfh: { label: 'Work from Home', class: 'status-wfh' },
      sabbatical: { label: 'Sabbatical', class: 'status-sabbatical' },
    };
    const s = statusMap[status] || { label: '--', class: 'status-none' };
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  const [focusField, setFocusField] = useState(null);

  const deleteSession = async (record) => {
    const sessionsOnDay = attendances.filter(a => a.date === record.date);
    if (sessionsOnDay.length <= 1) return;
    if (!window.confirm('Delete this session?')) return;
    try {
      if (!String(record.id).startsWith('new')) {
        await api.delete(`/attendance/${record.id}`);
      }
      setAttendances(prev => {
        const updated = prev.filter(a => a.id !== record.id);
        let sessionCount = 0;
        return updated.map(r => {
          if (r.date === record.date) {
            sessionCount++;
            return { ...r, sessionNum: sessionCount > 1 ? sessionCount : null };
          }
          return r;
        });
      });
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const addSession = (dateStr) => {
    const newId = `new-session-${dateStr}-${Date.now()}`;
    const newRecord = {
      id: newId,
      date: dateStr,
      clock_in: null,
      clock_out: null,
      total_hours: null,
      note: '',
      status: 'present',
      isNew: true,
      isPast: true,
      isToday: false,
      isFuture: false,
      sessionNum: null,
    };
    // Insert after the last record for this date
    setAttendances(prev => {
      const lastIdx = prev.reduce((acc, r, i) => r.date === dateStr ? i : acc, -1);
      const updated = [...prev];
      updated.splice(lastIdx + 1, 0, newRecord);
      // Update sessionNum for all records of this date
      let sessionCount = 0;
      return updated.map(r => {
        if (r.date === dateStr) {
          sessionCount++;
          return { ...r, sessionNum: sessionCount > 1 || r.date === dateStr ? sessionCount : null };
        }
        return r;
      });
    });
    // Auto-start editing the new session
    setTimeout(() => startEditing({ ...newRecord }, 'clock_in'), 50);
  };

  const startEditing = (record, field = 'clock_in') => {
    setEditingId(record.id);
    setFocusField(field);
    setEditValues({
      clock_in: formatTimeForInput(record.clock_in),
      clock_out: formatTimeForInput(record.clock_out),
      status: record.status || '--',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValues({});
  };

  const blurTimeout = React.useRef(null);
  const editValuesRef = React.useRef(editValues);
  editValuesRef.current = editValues;

  const handleBlur = (record) => {
    blurTimeout.current = setTimeout(() => {
      saveEditing(record);
    }, 150);
  };

  const handleFocus = () => {
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
      blurTimeout.current = null;
    }
  };

  const handleTab = (e, record, field) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
      blurTimeout.current = null;
    }

    if (field === 'clock_in') {
      // Only move to clock_out if clock_in has 4 digits
      const clockInDigits = (editValues.clock_in || '').replace(/[^0-9]/g, '');
      if (clockInDigits.length !== 4) return; // Don't tab if clock_in incomplete

      // Move focus to clock_out input in same row
      const row = e.target.closest('tr');
      if (row) {
        const clockOutInput = row.querySelectorAll('.time-input')[1];
        if (clockOutInput && !clockOutInput.disabled) {
          clockOutInput.focus();
          return;
        }
      }
      setFocusField('clock_out');
    } else if (field === 'clock_out') {
      // Save current row and move to next row's clock_in
      const currentIndex = attendances.findIndex(a => a.id === record.id);
      const nextRecord = attendances[currentIndex + 1];
      saveEditing(record).then(() => {
        if (nextRecord && !isWeekend(nextRecord.date)) {
          startEditing(nextRecord, 'clock_in');
        }
      });
    }
  };

  const isValidTime = (val) => {
    if (!val) return false;
    const match = val.match(/^(\d{2}):(\d{2})$/);
    if (!match) return false;
    const h = Number(match[1]);
    const m = Number(match[2]);
    return h >= 0 && h <= 23 && m >= 0 && m <= 59;
  };

  const saveEditing = async (record) => {
    if (saving) return;

    // Use ref to get latest values (avoids stale closure in blur handler)
    const currentValues = editValuesRef.current;

    // Clean up incomplete times — treat as empty
    let clockInVal = isValidTime(currentValues.clock_in) ? currentValues.clock_in : null;
    let clockOutVal = isValidTime(currentValues.clock_out) ? currentValues.clock_out : null;

    // If no clock_in, clear clock_out too
    if (!clockInVal) clockOutVal = null;

    // If nothing valid to save on a new record, just cancel the edit
    if (!clockInVal && (!record.id || String(record.id).startsWith('new-'))) {
      cancelEditing();
      return;
    }

    // Validate clock_out is after clock_in (only when both are valid)
    if (clockInVal && clockOutVal && clockOutVal <= clockInVal) {
      // Invalid clock_out — save with just clock_in
      clockOutVal = null;
    }

    setSaving(true);
    try {
      const dateStr = record.date;
      const clockIn = israelToUTC(dateStr, clockInVal);
      const clockOut = israelToUTC(dateStr, clockOutVal);

      const payload = {
        date: dateStr,
        clock_in: clockIn,
        clock_out: clockOut,
        status: currentValues.status === '--' ? 'present' : currentValues.status,
      };

      // Send record ID for existing sessions to update the correct one
      if (record.id && !String(record.id).startsWith('new-')) {
        payload.id = record.id;
      } else if (String(record.id).startsWith('new-session-')) {
        payload.new_session = true;
      }
      if (viewUserId) payload.user_id = viewUserId;

      const response = await api.post('/attendance', payload);

      if (response.data.status === 'success') {
        // Update locally for smooth UX
        const saved = response.data.data;
        setAttendances(prev => prev.map(a =>
          (a.id === record.id || a.id === saved.id) ? {
            ...a,
            id: saved.id,
            clock_in: saved.clock_in,
            clock_out: saved.clock_out,
            total_hours: saved.total_hours,
            note: saved.note || a.note,
            status: saved.status,
            isNew: false,
          } : a
        ));
        setEditingId(null);
        setEditValues({});
      } else {
        toast.error(response.data.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving attendance:', error.response || error);
      toast.error(error.response?.data?.message || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleLockMonth = async () => {
    setShowLockConfirm(false);
    setMonthLocked(true);
    try {
      await api.post('/attendance/lock', { month, user_id: viewUserId || undefined });
      toast.success('Month locked successfully');
    } catch (err) {
      setMonthLocked(false);
      console.error('Lock error:', err.response?.status, err.response?.data, err.message);
      toast.error(err.response?.data?.message || err.message || 'Failed to lock month');
    }
  };

  const handleUnlockMonth = async () => {
    try {
      await api.delete('/attendance/lock', { data: { month, user_id: viewUserId || currentUser?.id } });
      setMonthLocked(false);
      toast.success('Month unlocked');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to unlock month');
    }
  };

  // Determine if editing should be blocked
  const isEditBlocked = monthLocked && !isManager;

  const getExportData = () => {
    const statusLabel = (status) => {
      const map = { '--': '--', present: 'Present', late: 'Late', absent: 'Absent', approved_leave: 'Approved Leave', training: 'Training', holiday: 'Holiday', hag: 'Hag', weekend: 'Weekend', erev_hag: 'Erev Hag', vacation: 'Vacation', half_day_off: 'Half-Day Off', sick_leave: 'Sick Leave', child_sick: 'Child Sick Leave', miluim: 'Reserve Duty', childcare: 'Personal Childcare', business_trip: 'Business Trip Abroad', wfh: 'Work from Home', sabbatical: 'Sabbatical' };
      return map[status] || '--';
    };
    const seenExportDates = new Set();
    return attendances.map((r) => {
      const hours = r.total_hours ? parseFloat(r.total_hours) : null;
      const isFirstRowOfDay = !seenExportDates.has(r.date);
      if (isFirstRowOfDay) seenExportDates.add(r.date);
      const dayTotal = dailyTotals[r.date]?.totalHours || 0;
      const isWeekendOrHoliday = isWeekend(r.date) || isHolidayDate(r.date);
      return {
        Date: formatDate(r.date),
        'Clock In': formatTime(r.clock_in),
        'Clock Out': formatTime(r.clock_out),
        'Worked Hours': formatHours(hours),
        '+/- Hours': (() => { const ds = holidayTypes[r.date] === 'half_day' ? STANDARD_HOURS / 2 : STANDARD_HOURS; if (isFirstRowOfDay && dayTotal > 0 && !isWeekendOrHoliday && ds - dayTotal > 0.01) return '-' + formatHours(ds - dayTotal); if (isFirstRowOfDay && dayTotal > 0 && !isWeekendOrHoliday && dayTotal - ds > 0.01) return '+' + formatHours(dayTotal - ds); return '--'; })(),
        Status: statusLabel(r.status),
        Note: r.note || '',
      };
    });
  };

  const getMonthLabel = () => {
    const [y, m] = month.split('-');
    const date = new Date(y, m - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const fixRTL = (text) => {
    if (!text || typeof text !== 'string') return text;
    // Split into runs of Hebrew vs non-Hebrew characters
    return text.replace(/[\u0590-\u05FF\uFB1D-\uFB4F]+/g, (match) => match.split('').reverse().join(''))
      // Reverse overall order of words if the string starts with Hebrew
      .replace(/^([\u0590-\u05FF\uFB1D-\uFB4F].*)$/gm, (line) => {
        const parts = line.split(/(\s+)/);
        return parts.reverse().join('');
      });
  };

  const exportPDF = () => {
    try {
      const data = getExportData();
      if (data.length === 0) { toast.error('No data to export'); return; }
      const doc = new jsPDF();
      doc.addFileToVFS('Rubik-Regular.ttf', rubikBase64);
      doc.addFont('Rubik-Regular.ttf', 'Rubik', 'normal');
      doc.setFont('Rubik');
      doc.setFontSize(16);
      doc.text(`Attendance - ${getMonthLabel()}`, 14, 18);
      autoTable(doc, {
        startY: 26,
        head: [['Date', 'Clock In', 'Clock Out', 'Worked Hours', '+/- Hours', 'Status', 'Note']],
        body: data.map((r) => [r.Date, r['Clock In'], r['Clock Out'], r['Worked Hours'], r['+/- Hours'], fixRTL(r.Status), fixRTL(r.Note)]),
        styles: { fontSize: 8, cellPadding: 3, font: 'Rubik' },
        headStyles: { fillColor: [71, 182, 227], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
      doc.save(`Attendance_${month}.pdf`);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('PDF export failed: ' + err.message);
    }
  };

  const exportExcel = () => {
    const data = getExportData();
    if (data.length === 0) { toast.error('No data to export'); return; }
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Attendance_${month}.xlsx`);
    toast.success('Excel exported successfully');
  };

  return (
    <div className="attendance-page">
      <div className="page-header">
        <h1>{viewUserName ? <>Time Log &rsaquo; {viewUserName}</> : 'Time Log'}</h1>
        <div className="header-actions">
          {/* Lock/Unlock button */}
          {monthLocked ? (
            isManager ? (
              <button className="btn-lock-toggle is-locked unlock" onClick={handleUnlockMonth} title="Unlock this month">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Unlock
              </button>
            ) : (
              <span className="btn-lock-toggle is-locked">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Locked
              </span>
            )
          ) : (!viewUserId || isManager) ? (
            <button
              className="btn-lock-toggle"
              onClick={() => setShowLockConfirm(true)}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 4.83 3.67" />
              </svg>
              Lock Timesheet
            </button>
          ) : null}
          <button className="btn-export btn-export-pdf" onClick={exportPDF} title="Export as PDF">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <polyline points="9 15 12 18 15 15" />
            </svg>
            Export PDF
          </button>
          <button className="btn-export btn-export-excel" onClick={exportExcel} title="Export as Excel">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <polyline points="9 15 12 18 15 15" />
            </svg>
            Export Excel
          </button>
          <div className="month-nav">
            <button className="btn-month-arrow" onClick={() => {
              const [y, m] = month.split('-').map(Number);
              const d = new Date(y, m - 2, 1);
              if (d >= new Date(2022, 0, 1)) setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
            }} title="Previous month" disabled={month === '2022-01'}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="cal-dropdown" ref={calRef}>
              <button className="month-picker" onClick={() => { setCalYear(Number(month.split('-')[0])); setCalOpen(!calOpen); }}>
                {new Date(Number(month.split('-')[0]), Number(month.split('-')[1]) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '0.5rem' }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </button>
              {calOpen && (
                <div className="cal-popup">
                  <div className="cal-year-nav">
                    <button onClick={() => { if (calYear > 2022) setCalYear(calYear - 1); }} disabled={calYear <= 2022}>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <span className="cal-year-label">{calYear}</span>
                    <button onClick={() => { if (calYear < 2040) setCalYear(calYear + 1); }} disabled={calYear >= 2040}>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                  </div>
                  <div className="cal-months">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((name, i) => {
                      const m = i + 1;
                      const isFuture = false;
                      const isSelected = calYear === Number(month.split('-')[0]) && m === Number(month.split('-')[1]);
                      return (
                        <button
                          key={m}
                          className={`cal-month-btn${isSelected ? ' selected' : ''}${isFuture ? ' disabled' : ''}`}
                          disabled={isFuture}
                          onClick={() => {
                            setMonth(`${calYear}-${String(m).padStart(2, '0')}`);
                            setCalOpen(false);
                          }}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="cal-footer">
                    <button className="cal-footer-btn" onClick={() => { setMonth(''); setCalOpen(false); setTimeout(() => { const now = new Date(); setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`); }, 0); }}>
                      This month
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button className="btn-month-arrow" onClick={() => {
              const [y, m] = month.split('-').map(Number);
              const d = new Date(y, m, 1);
              const now = new Date();
              const current = new Date(now.getFullYear(), now.getMonth(), 1);
              if (d.getFullYear() <= 2040) setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
            }} title="Next month" disabled={(() => {
              const [y, m] = month.split('-').map(Number);
              return y >= 2040 && m >= 12;
            })()}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : attendances.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <p>No attendance records for this month</p>
        </div>
      ) : (
        <>
          {/* Summary Section */}
          {(() => {
            // Group by date and sum hours per day
            const dailyHours = {};
            const weekendHours = {};
            attendances.forEach(r => {
              if (isWeekend(r.date) || isHolidayDate(r.date)) {
                // Track weekend/holiday hours separately
                if (!weekendHours[r.date]) weekendHours[r.date] = 0;
                if (r.total_hours) weekendHours[r.date] += parseFloat(r.total_hours) || 0;
                return;
              }
              if (!dailyHours[r.date]) dailyHours[r.date] = 0;
              if (r.total_hours) dailyHours[r.date] += parseFloat(r.total_hours) || 0;
            });

            // Calculate totals based on daily sums
            const workDayDates = Object.keys(dailyHours).filter(d => dailyHours[d] > 0);
            const totalWeekendHours = Object.values(weekendHours).reduce((sum, h) => sum + h, 0);
            const totalHours = workDayDates.reduce((sum, d) => sum + dailyHours[d], 0) + totalWeekendHours;

            // Required hours = all working days in month up to yesterday × 8.5
            // Only API holidays (Jewish calendar) are excluded, not manual attendance statuses
            const [mYear, mMonth] = month.split('-').map(Number);
            const today = new Date();
            const lastDayOfMonth = new Date(mYear, mMonth, 0).getDate();
            const isFutureMonth = (mYear > today.getFullYear()) || (mYear === today.getFullYear() && mMonth > today.getMonth() + 1);
            const isCurrentMonth = (mYear === today.getFullYear() && mMonth === today.getMonth() + 1);
            const endDay = isFutureMonth ? 0 : isCurrentMonth ? Math.max(today.getDate() - 1, 0) : lastDayOfMonth;
            let expectedHours = 0;
            let workingDaysInMonth = 0;
            for (let d = 1; d <= endDay; d++) {
              const dateStr = `${month}-${String(d).padStart(2, '0')}`;
              if (isWeekend(dateStr) || isHolidayDate(dateStr)) continue;
              if (holidayTypes[dateStr] === 'half_day') {
                workingDaysInMonth += 0.5; // Erev hag = half day
                expectedHours += STANDARD_HOURS / 2;
              } else {
                workingDaysInMonth++;
                expectedHours += STANDARD_HOURS;
              }
            }
            const difference = expectedHours - totalHours;

            // Present = actual days attended with clock times
            const presentDays = workDayDates.length;

            // Event days = unique days with special statuses (no clock times)
            const eventStatuses = ['sick_leave', 'child_sick', 'vacation', 'approved_leave', 'training', 'miluim', 'childcare', 'business_trip', 'sabbatical', 'half_day_off'];
            const eventDays = new Set(attendances.filter(r => eventStatuses.includes(r.status)).map(r => r.date)).size;

            return (
              <div className="summary-section">
                <div className="summary-card">
                  <span className="summary-label">Total Logged Hours</span>
                  <span className="summary-value">{formatHours(totalHours)}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Required Hours</span>
                  <span className="summary-value">{formatHours(expectedHours)}</span>
                </div>
                {Math.abs(difference) > 0.01 && (
                  <div className={`summary-card ${difference > 0 ? 'under' : 'excess'}`}>
                    <span className="summary-label">+/- Hours</span>
                    <span className="summary-value">{difference > 0 ? '-' : '+'}{formatHours(Math.abs(difference))}</span>
                  </div>
                )}
                <div className="summary-card">
                  <span className="summary-label">Work Days</span>
                  <span className="summary-value">{workingDaysInMonth}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Present</span>
                  <span className="summary-value">{presentDays}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Event Days</span>
                  <span className="summary-value">{eventDays}</span>
                </div>
              </div>
            );
          })()}

          <div className="attendance-table-wrapper">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Worked Hours</th>
                <th>+/- Hours</th>
                <th>Status</th>
                <th style={{ width: '30px' }}></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(() => { seenDates.current = new Set(); return null; })()}
              {attendances.map((record, idx) => {
                const isFirstRowOfDay = !seenDates.current.has(record.date);
                if (isFirstRowOfDay) seenDates.current.add(record.date);
                const isLastRowOfDay = idx === attendances.length - 1 || attendances[idx + 1]?.date !== record.date;
                const dayTotal = dailyTotals[record.date]?.totalHours || 0;
                return (
                <tr
                  key={record.id}
                  className={`${record.isNew ? 'no-record' : ''} ${(isWeekend(record.date) || isHolidayDate(record.date)) ? 'weekend' : ''} ${record.isToday ? 'today' : ''} ${record.isFuture && record.isNew ? 'future-row' : ''}`}
                >
                  <td className="date-cell">
                    {formatDate(record.date)}
                    {holidayNames[record.date] && <span className="holiday-label">{holidayNames[record.date]}</span>}
                    {record.sessionNum && <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', display: 'block' }}>Session {record.sessionNum}</span>}
                  </td>

                  <td onDoubleClick={() => !editingId && !record.isFuture && !isEditBlocked && startEditing(record, 'clock_in')} style={{ cursor: (record.isFuture || isEditBlocked) ? 'default' : (editingId ? 'default' : 'pointer') }}>
                    {editingId === record.id && !record.isFuture ? (
                      <input
                        type="text"
                        placeholder="--:--"
                        autoFocus={focusField === 'clock_in'}
                        value={editValues.clock_in || ''}
                        onChange={(e) => {
                          let digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                          const val = formatTimeWithDashes(digits);
                          setEditValues(prev => ({ ...prev, clock_in: val, status: (digits.length > 0 && prev.status === '--') ? 'present' : prev.status }));
                        }}
                        onBlur={() => handleBlur(record)}
                        onFocus={handleFocus}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); if (blurTimeout.current) { clearTimeout(blurTimeout.current); blurTimeout.current = null; } saveEditing(record); return; }
                          if (e.key === 'Backspace') {
                            e.preventDefault();
                            const currentDigits = (editValues.clock_in || '').replace(/[^0-9]/g, '');
                            if (currentDigits.length > 0) {
                              const newDigits = currentDigits.slice(0, -1);
                              setEditValues(prev => ({ ...prev, clock_in: formatTimeWithDashes(newDigits) }));
                            }
                            return;
                          }
                          handleTab(e, record, 'clock_in');
                        }}
                        className="time-input"
                      />
                    ) : (
                      formatTime(record.clock_in)
                    )}
                  </td>
                  <td onDoubleClick={() => {
                    if (record.isFuture || isEditBlocked) return;
                    // Only allow editing clock_out if clock_in has a value
                    const hasClockIn = editingId === record.id
                      ? (editValues.clock_in || '').replace(/[^0-9]/g, '').length === 4
                      : !!record.clock_in;
                    if (!editingId && hasClockIn) startEditing(record, 'clock_out');
                  }} style={{ cursor: (editingId || isEditBlocked) ? 'default' : 'pointer' }}>
                    {editingId === record.id && !record.isFuture ? (
                      <input
                        type="text"
                        placeholder="--:--"
                        autoFocus={focusField === 'clock_out'}
                        value={editValues.clock_out || ''}
                        disabled={!(editValues.clock_in || '').replace(/[^0-9]/g, '').length}
                        onChange={(e) => {
                          let digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                          const val = formatTimeWithDashes(digits);
                          setEditValues(prev => ({ ...prev, clock_out: val, status: (digits.length > 0 && prev.status === '--') ? 'present' : prev.status }));
                        }}
                        onBlur={() => handleBlur(record)}
                        onFocus={handleFocus}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); if (blurTimeout.current) { clearTimeout(blurTimeout.current); blurTimeout.current = null; } saveEditing(record); return; }
                          if (e.key === 'Backspace') {
                            e.preventDefault();
                            const currentDigits = (editValues.clock_out || '').replace(/[^0-9]/g, '');
                            if (currentDigits.length > 0) {
                              const newDigits = currentDigits.slice(0, -1);
                              setEditValues(prev => ({ ...prev, clock_out: formatTimeWithDashes(newDigits), status: (newDigits.length > 0 && prev.status === '--') ? 'present' : prev.status }));
                            }
                            return;
                          }
                          handleTab(e, record, 'clock_out');
                        }}
                        className="time-input"
                      />
                    ) : (
                      formatTime(record.clock_out)
                    )}
                  </td>
                  <td>
                    {editingId === record.id ? '--' : (!record.clock_in && !record.clock_out) ? '--' : formatHours(record.total_hours ? parseFloat(record.total_hours) : null)}
                  </td>
                  {(() => {
                    const isNormalDay = !isWeekend(record.date) && !isHolidayDate(record.date);
                    const dayStandard = holidayTypes[record.date] === 'half_day' ? STANDARD_HOURS / 2 : STANDARD_HOURS;
                    const extraVal = isNormalDay ? (sessionExtras[record.id] || 0) : 0;
                    const missingVal = isFirstRowOfDay && isNormalDay && dayTotal > 0 && dayStandard - dayTotal > 0.01
                      ? dayStandard - dayTotal : 0;
                    if (extraVal > 0.01) {
                      return <td className="excess-cell">+{formatHours(extraVal)}</td>;
                    } else if (missingVal > 0.01) {
                      return <td className="under-std-cell">-{formatHours(missingVal)}</td>;
                    } else {
                      return <td>--</td>;
                    }
                  })()}
                  <td style={{ cursor: (isEditBlocked || !isFirstRowOfDay) ? 'default' : 'pointer' }}>
                    {isFirstRowOfDay ? (
                      <span className="status-badge-wrapper" onClick={(e) => {
                        if (editingId || isEditBlocked) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const dropdownHeight = 480;
                        const spaceBelow = window.innerHeight - rect.bottom - 10;
                        const spaceAbove = rect.top - 10;
                        const openUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
                        const maxH = openUp ? Math.min(spaceAbove, dropdownHeight) : Math.min(spaceBelow, dropdownHeight);
                        setStatusDropdownPos({
                          top: openUp ? rect.top - maxH - 4 : rect.bottom + 4,
                          left: rect.left,
                          openUp,
                          maxHeight: maxH
                        });
                        setStatusDropdownRecord(record);
                        startEditing(record);
                      }}>
                        {getStatusBadge(record.status, record.isNew)}
                      </span>
                    ) : null}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {isLastRowOfDay && !record.isFuture && !isEditBlocked && (
                      <button
                        onClick={() => addSession(record.date)}
                        title="Add session"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#001478', fontSize: '1.3rem', fontWeight: 700, lineHeight: 1 }}
                      >+</button>
                    )}
                    {!isFirstRowOfDay && !record.isFuture && !isEditBlocked && (
                      <button
                        onClick={() => deleteSession(record)}
                        title="Delete session"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#dc3545', fontSize: '1.1rem', fontWeight: 700, lineHeight: 1, marginLeft: '6px' }}
                      >&times;</button>
                    )}
                  </td>
                  <td className="note-cell">
                    <button className={`note-icon ${record.note ? 'has-note' : ''}`} onClick={(e) => !record.isFuture && !isEditBlocked && openNotePopup(record, e)} title={record.note || 'Add note'} disabled={record.isFuture || isEditBlocked}>
                      {record.note ? (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="none">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td className="attach-cell">
                    <button
                      className={`attach-icon ${(attachments[record.date] || []).length > 0 ? 'has-files' : ''}`}
                      onClick={(e) => !record.isFuture && !isEditBlocked && openAttachPopup(record, e)}
                      disabled={record.isFuture || isEditBlocked}
                      title={`${(attachments[record.date] || []).length} file(s)`}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      {(attachments[record.date] || []).length > 0 && (
                        <span className="attach-file-count">{(attachments[record.date] || []).length}</span>
                      )}
                    </button>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      {/* Status dropdown via portal */}
      {statusDropdownRecord && statusDropdownPos && ReactDOM.createPortal(
        <div
          ref={statusDropdownRef}
          className="status-dropdown"
          style={{
            position: 'fixed',
            top: statusDropdownPos.top,
            left: statusDropdownPos.left,
            zIndex: 99999,
            background: 'var(--dropdown-bg)',
            border: '1px solid var(--border-default)',
            borderRadius: '12px',
            boxShadow: 'var(--dropdown-shadow)',
            minWidth: '160px',
            maxHeight: statusDropdownPos.maxHeight || 480,
            overflowY: 'auto',
          }}
        >
          {['--', 'present', 'absent', 'approved_leave', 'training', 'vacation', 'half_day_off', 'sick_leave', 'child_sick', 'miluim', 'childcare', 'business_trip', 'wfh', 'sabbatical'].map(status => (
            <div
              key={status}
              onClick={() => {
                const record = statusDropdownRecord;
                if (status === '--') {
                  if (record.id && !String(record.id).startsWith('new-')) {
                    setAttendances(prev => prev.map(a =>
                      a.id === record.id ? { ...a, clock_in: null, clock_out: null, total_hours: null, status: '--', isNew: true } : a
                    ));
                    api.delete(`/attendance/${record.id}`)
                      .catch(() => toast.error('Failed to delete'));
                  }
                } else {
                  const dateStr = record.date;
                  const clearsHours = status === 'vacation';
                  const clockIn = clearsHours ? null : israelToUTC(dateStr, editValues.clock_in);
                  const clockOut = clearsHours ? null : israelToUTC(dateStr, editValues.clock_out);
                  setAttendances(prev => prev.map(a =>
                    a.date === dateStr ? {
                      ...a,
                      clock_in: clearsHours ? null : a.clock_in,
                      clock_out: clearsHours ? null : a.clock_out,
                      total_hours: clearsHours ? null : a.total_hours,
                      status,
                      isNew: false
                    } : a
                  ));
                  const payload = {
                    date: dateStr,
                    clock_in: clockIn,
                    clock_out: clockOut,
                    status: status,
                  };
                  if (record.id && !String(record.id).startsWith('new-')) payload.id = record.id;
                  if (viewUserId) payload.user_id = viewUserId;
                  api.post('/attendance', payload).then((res) => {
                    const saved = res.data.data;
                    setAttendances(prev => prev.map(a =>
                      a.date === dateStr ? { ...a, id: saved.id, clock_in: saved.clock_in, clock_out: saved.clock_out, total_hours: saved.total_hours, status: saved.status, isNew: false } : a
                    ));
                  }).catch(() => toast.error('Failed to save'));
                }
                if (blurTimeout.current) {
                  clearTimeout(blurTimeout.current);
                  blurTimeout.current = null;
                }
                setStatusDropdownRecord(null);
                setStatusDropdownPos(null);
                setEditingId(null);
                setEditValues({});
              }}
              style={{
                padding: '8px 14px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.85rem',
                transition: 'background 0.15s',
                color: 'var(--dropdown-text)',
              }}
              onMouseEnter={(e) => e.target.style.background = 'var(--dropdown-hover)'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              {status === '--' ? 'Clear' : { present: 'Present', absent: 'Absent', approved_leave: 'Approved Leave', training: 'Training', holiday: 'Holiday', weekend: 'Weekend', vacation: 'Vacation', half_day_off: 'Half-Day Off', sick_leave: 'Sick Leave', child_sick: 'Child Sick Leave', miluim: 'Reserve Duty', childcare: 'Personal Childcare', business_trip: 'Business Trip Abroad', wfh: 'Work from Home', sabbatical: 'Sabbatical' }[status] || status}
            </div>
          ))}
        </div>,
        document.body
      )}
      {/* Note popup via portal */}
      {notePopup && ReactDOM.createPortal(
        <div
          ref={notePopupRef}
          className="note-popup"
          style={{
            position: 'fixed',
            top: notePopup.pos.top,
            left: notePopup.pos.left,
            zIndex: 99999,
          }}
        >
          <div className="note-popup-header">
            <span>Note - {formatDate(notePopup.record.date)}</span>
            <button className="note-popup-close" onClick={() => { setNotePopup(null); setNoteText(''); }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <textarea
            className="note-textarea"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note..."
            rows={3}
            autoFocus
          />
          <button className="btn-save-note" onClick={saveNote}>Save</button>
        </div>,
        document.body
      )}
      {/* Attachment popup via portal */}
      {attachPopup && ReactDOM.createPortal(
        <div
          ref={attachPopupRef}
          className="attach-popup"
          style={{
            position: 'fixed',
            top: attachPopup.pos.top,
            left: attachPopup.pos.left,
            zIndex: 99999,
          }}
        >
          <div className="attach-popup-header">
            <span>Attachments - {formatDate(attachPopup.record.date)}</span>
            <button className="note-popup-close" onClick={() => setAttachPopup(null)}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="attach-file-list">
            {(attachments[attachPopup.record.date] || []).length === 0 ? (
              <div className="attach-empty">No files attached</div>
            ) : (
              (attachments[attachPopup.record.date] || []).map(file => (
                <div key={file.id} className="attach-file-item">
                  <span className="attach-filename" onClick={() => handleFileDownload(file)} title="Download">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    {file.filename}
                  </span>
                  <button className="attach-delete-btn" onClick={() => handleFileDelete(attachPopup.record.date, file.id)} title="Delete">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.zip,.rar,.7z"
            style={{ display: 'none' }}
          />
          <button
            className="attach-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {uploading ? 'Uploading...' : 'Upload File'}
          </button>
        </div>,
        document.body
      )}
      {/* Lock confirmation modal */}
      {showLockConfirm && ReactDOM.createPortal(
        <div className="lock-overlay" onClick={() => setShowLockConfirm(false)}>
          <div className="lock-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lock-confirm-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="var(--mars-orange)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3>Lock {getMonthLabel()}?</h3>
            <p>This confirms that your hours for this month are final. After locking, only a manager or admin can edit your attendance.</p>
            <div className="lock-confirm-actions">
              <button className="btn-cancel" onClick={() => setShowLockConfirm(false)}>Cancel</button>
              <button className="btn-lock-confirm" onClick={handleLockMonth}>Yes, Lock Timesheet</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Attendance;
